/**
 * The crop colour classes (tokens.css --color-crop-N), as literal class names
 * so Tailwind generates them. A light fill with dark text, in the same hue.
 */
const CROP_COLOUR_CLASSES = [
  'bg-crop-1 text-crop-1-text',
  'bg-crop-2 text-crop-2-text',
  'bg-crop-3 text-crop-3-text',
  'bg-crop-4 text-crop-4-text',
  'bg-crop-5 text-crop-5-text',
  'bg-crop-6 text-crop-6-text',
  'bg-crop-7 text-crop-7-text',
  'bg-crop-8 text-crop-8-text',
  'bg-crop-9 text-crop-9-text',
  'bg-crop-10 text-crop-10-text',
] as const;

/**
 * The same crops' colours as chart marks (bars), in each hue's dark shade so
 * they pass 3:1 against white — the light pill fill wouldn't.
 */
const CROP_MARK_CLASSES = [
  'fill-crop-1-text',
  'fill-crop-2-text',
  'fill-crop-3-text',
  'fill-crop-4-text',
  'fill-crop-5-text',
  'fill-crop-6-text',
  'fill-crop-7-text',
  'fill-crop-8-text',
  'fill-crop-9-text',
  'fill-crop-10-text',
] as const;

/**
 * Common crops get a colour that suits them (wheat gold, canola yellow,
 * pulses green), matched on a word in the crop name. Index into
 * CROP_COLOUR_CLASSES. Checked in order, so "chickpea" is caught before "pea".
 */
const KNOWN_CROPS: [keyword: string, colour: number][] = [
  ['wheat', 0],
  ['durum', 0],
  ['barley', 1],
  ['canola', 2],
  ['lupin', 3],
  ['chickpea', 3],
  ['lentil', 3],
  ['bean', 3],
  ['pea', 3],
  ['oat', 4],
  ['sorghum', 5],
  ['maize', 5],
  ['corn', 5],
  ['cotton', 6],
  ['pasture', 7],
  ['vetch', 8],
  ['fallow', 9],
];

/** A small, stable hash so any other crop always gets the same colour. */
const hashName = (name: string): number => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 2147483647;
  }
  return hash;
};

/**
 * The colour classes for a crop (OP-2501). Fixed by the crop's name, so a
 * crop is the same colour in every year and every view — not by its place in
 * a list, as chart series are. Two uncommon crops can share a colour; the
 * name is always shown beside it.
 */
const cropColourIndex = (cropName: string): number => {
  const name = cropName.trim().toLowerCase();
  const known = KNOWN_CROPS.find(([keyword]) => name.includes(keyword));
  return known ? known[1] : hashName(name) % CROP_COLOUR_CLASSES.length;
};

export const cropColourClasses = (cropName: string): string =>
  CROP_COLOUR_CLASSES[cropColourIndex(cropName)];

/** The crop's colour for a chart bar — the same hue as its pill in the grid. */
export const cropMarkClass = (cropName: string): string =>
  CROP_MARK_CLASSES[cropColourIndex(cropName)];
